import { BlitzPage, Routes } from "@blitzjs/next"
import { useMutation, useQuery } from "@blitzjs/rpc"
import { SScaleLoader } from "@superwall-me/sui"
import type { ApplicationSearchResult } from "app/api-utils/trpc/routers/blitzMigration/applications"
import selectOrganization from "app/auth/mutations/selectOrganization"
import { useEnsureSession, useSession } from "app/authv2/use-session"
import { ApplicationSearchCard } from "app/commandbar/CommandBar"
import SidebarLayout from "app/core/layouts/SidebarLayout"
import getSingleUserOrganizations from "app/users/queries/getSingleUserOrganization"
import { useRouter } from "next/router"
import { useCallback, useEffect, useState } from "react"

const OrganizationSelector = ({}) => {
  useEnsureSession()
  const [memberships, error] = useQuery(getSingleUserOrganizations, null, { suspense: false })
  // This user has no memberships, redirect them to create application page to finish
  // signup
  const router = useRouter()
  useEffect(() => {
    if (memberships) {
      if (memberships.length === 0) {
        router.push(Routes.OnboardingNewApplicationPage())
      }
    }
  }, [router, memberships])

  const [selectOrganizationMutation] = useMutation(selectOrganization)
  const [loading, setLoading] = useState(false)

  const onSelect = useCallback(
    async (application: ApplicationSearchResult) => {
      setLoading(true)
      await selectOrganizationMutation({
        organizationId: application.organization.id,
      })

      router.push(Routes.ShowApplicationDashboardPage({ applicationId: application.id }))
      setLoading(false)
    },
    [router, selectOrganizationMutation]
  )

  return (
    <div>
      <main
        className={`dark:bg-grayscale-darkest max-h-screen h-[70vh] flex flex-row items-center justify-center`}
      >
        {memberships && !loading ? (
          <div className="w-full max-w-[800px] ">
            <div className="flex flex-col gap-y-2">
              <Header />
              <ApplicationSearchCard onSelect={onSelect}></ApplicationSearchCard>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-[800px] flex flex-row items-center justify-center">
            <SScaleLoader></SScaleLoader>
          </div>
        )}
      </main>
    </div>
  )
}

const Header = () => {
  const { data: user } = useSession()

  return (
    <div className="text-left py-4 px-4 pr-8  rounded-xl flex flex-col justify-start items-start lg:flex-row gap-y-3 lg:justify-between gap-x-4 lg:items-center">
      <div className="flex flex-col items-start lg:flex-row lg:items-center gap-6 lg:gap-5">
        <span className="text-accent-light waving-hand">
          <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 48 48">
            <title>waving-hand</title>
            <g>
              <path
                d="M37.843,8.814a2.851,2.851,0,0,0-2.49,3.362c.027.224.69,5.166,1.353,8.308L22.829,6.607a2.5,2.5,0,0,0-3.708.192,2.6,2.6,0,0,0,.273,3.445l8.032,8.031a1,1,0,0,1-1.415,1.414L15.051,8.729a2.5,2.5,0,0,0-3.708.191,2.6,2.6,0,0,0,.273,3.445l10.86,10.859a1,1,0,0,1-1.414,1.415L10.909,14.486a2.6,2.6,0,0,0-3.445-.273,2.5,2.5,0,0,0-.191,3.708L17.526,28.174a1,1,0,0,1-1.414,1.414L9.495,22.972A2.6,2.6,0,0,0,6.05,22.7a2.5,2.5,0,0,0-.191,3.708l8.7,8.7,5.8,5.8a14,14,0,0,0,19.8,0l1.735-1.736a13.962,13.962,0,0,0,3.391-14.3c-1.465-4.377-3.545-9.312-4.542-13.795A2.832,2.832,0,0,0,37.843,8.814Z"
                fill="#ffd764"
              ></path>{" "}
              <path
                d="M35.209,34.533a1,1,0,0,1-1.415,0c-3.9-3.9-3.581-10.56.707-14.849A1,1,0,1,1,35.916,21.1c-3.51,3.509-3.827,8.9-.707,12.021A1,1,0,0,1,35.209,34.533Z"
                fill="#e2ac4b"
              ></path>{" "}
              <path
                d="M35,8a1,1,0,0,1-.858-.485A9.978,9.978,0,0,0,26,3a1,1,0,0,1,0-2,12.007,12.007,0,0,1,9.857,5.485A1,1,0,0,1,35,8Z"
                fill="#b3b3b3"
              ></path>{" "}
              <path
                d="M31,11a1,1,0,0,1-.948-.684A3.44,3.44,0,0,0,27,8a1,1,0,0,1,0-2c3.679,0,4.9,3.533,4.948,3.684A1,1,0,0,1,31,11Z"
                fill="#b3b3b3"
              ></path>{" "}
              <path
                d="M10,41a.992.992,0,0,1-.143-.01c-.077-.011-7.754-1.2-8.848-8.848a1,1,0,1,1,1.98-.284c.878,6.15,6.9,7.115,7.155,7.152A1,1,0,0,1,10,41Z"
                fill="#b3b3b3"
              ></path>{" "}
              <path
                d="M10,36a1,1,0,0,1-.316-.051,7.817,7.817,0,0,1-4.632-4.633,1,1,0,1,1,1.9-.632,5.791,5.791,0,0,0,3.368,3.367A1,1,0,0,1,10,36Z"
                fill="#b3b3b3"
              ></path>
            </g>
          </svg>
        </span>
        <div>
          <div className="flex flex-row items-center gap-2">
            <p className="text-2xl flex items-center justify-center">
              Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            </p>
          </div>

          <p className="text opacity-40 text-lg -mt-1">{`Select an app to continue`}</p>
        </div>
      </div>
    </div>
  )
}

const SelectOrganization: BlitzPage = () => {
  return <OrganizationSelector />
}

SelectOrganization.suppressFirstRenderFlicker = true
SelectOrganization.getLayout = (page) => <SidebarLayout>{page}</SidebarLayout>

export default SelectOrganization
